import React, { useEffect, useMemo, useState } from 'react';
import { fetchSuppliers, deleteSupplier } from '@/api';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { SupplierDialog } from './SupplierDialog';
import { BulkReturnDialog } from './BulkReturnDialog';
import type { Supplier } from '@/types/supplier';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface Props {
  onSelect?: (id: string) => void;
  onSelectReturns?: (id: string) => void;
}

export function SuppliersTable({ onSelect, onSelectReturns }: Props) {
  const currencySymbol = useCurrencySymbol();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState<Supplier | null>(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10 });

  const load = async () => {
    try {
      const res = await fetchSuppliers();
      setSuppliers(res.data);
    } catch {
      toast.error('Failed to load suppliers');
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return suppliers;
    const q = search.toLowerCase();
    return suppliers.filter(s =>
      s.name.toLowerCase().includes(q) ||
      (s.contactPerson || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q)
    );
  }, [suppliers, search]);

  const paged = useMemo(() => {
    const start = (pagination.current_page - 1) * pagination.per_page;
    return filtered.slice(start, start + pagination.per_page);
  }, [filtered, pagination]);

  const total = filtered.length;
  const last_page = Math.max(1, Math.ceil(total / pagination.per_page));

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search suppliers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, current_page: 1 })); }}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReturnDialogOpen(true)}>
            <RotateCcw className="mr-1 h-4 w-4" /> Return Items
          </Button>
          <Button onClick={() => { setCurrent(null); setDialogOpen(true); }}>
            <Plus className="mr-1" /> Add Supplier
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Contact Person</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Payment Terms</TableHead>
            <TableHead className="text-right">Credit</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paged.map(s => (
            <TableRow key={s.id}>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.contactPerson}</TableCell>
              <TableCell>{s.email}</TableCell>
              <TableCell>{s.phone}</TableCell>
              <TableCell className="capitalize">{s.paymentTerm}</TableCell>
              <TableCell className="text-right">
                {(s.creditBalance || 0) > 0
                  ? <span className="text-green-600 font-medium">{currencySymbol}{(s.creditBalance || 0).toFixed(2)}</span>
                  : <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => { setCurrent(s); setDialogOpen(true); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={async () => {
                  if (confirm(`Delete ${s.name}?`)) {
                    await deleteSupplier(s.id);
                    toast.success('Deleted');
                    load();
                  }
                }}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onSelect?.(s.id)}>
                  View Invoices
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onSelectReturns?.(s.id)}>
                  View Returns
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center justify-between py-3">
        <div className="text-sm text-muted-foreground">
          {(() => {
            if (!total) return 'Showing 0-0 of 0 suppliers';
            const start = (pagination.current_page - 1) * pagination.per_page + (paged.length ? 1 : 0);
            const end = (pagination.current_page - 1) * pagination.per_page + paged.length;
            return `Showing ${start}-${end} of ${total} suppliers`;
          })()}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Per page:</span>
            <Select
              value={String(pagination.per_page)}
              onValueChange={(v) => setPagination(prev => ({ ...prev, per_page: Number(v), current_page: 1 }))}
            >
              <SelectTrigger className="h-8 w-[90px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {last_page > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, current_page: p.current_page - 1 }))}
                disabled={pagination.current_page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, current_page: p.current_page + 1 }))}
                disabled={pagination.current_page >= last_page}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <SupplierDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplier={current}
        onSaved={load}
      />

      <BulkReturnDialog
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        onCompleted={load}
      />
    </>
  );
}
